import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, Vote, CheckCircle2, Calendar, MapPin, Lock, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { nb } from "@/lib/format";

const CHOICES = [
  { key: "for", label: "For" },
  { key: "mot", label: "Mot" },
  { key: "avstar", label: "Avstår" },
];

function fmtDate(s) {
  if (!s) return "";
  try {
    const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
    return d.toLocaleString("nb-NO", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return s;
  }
}

function countdownText(s) {
  try {
    const d = new Date(s && s.length === 10 ? `${s}T00:00:00` : s);
    const diff = Math.ceil((d.getTime() - Date.now()) / 86400000);
    if (Number.isNaN(diff)) return null;
    if (diff < 0) return { text: "møtet har startet", past: true };
    if (diff === 0) return { text: "i dag", past: false };
    if (diff === 1) return { text: "i morgen", past: false };
    return { text: `om ${diff} dager`, past: false };
  } catch {
    return null;
  }
}

function ChoicePicker({ value, disabled, onPick, testidPrefix }) {
  return (
    <div className="flex gap-2">
      {CHOICES.map((c) => (
        <button
          key={c.key}
          type="button"
          data-testid={`${testidPrefix}-${c.key}`}
          disabled={disabled}
          onClick={() => onPick(c.key)}
          className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            value === c.key
              ? c.key === "for"
                ? "border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                : c.key === "mot"
                ? "border-red-500 bg-red-500/15 text-red-700 dark:text-red-300"
                : "border-foreground/40 bg-muted text-foreground"
              : "border-border hover:bg-muted"
          }`}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

export default function PreVote() {
  const { meetingId, shareholderId } = useParams();
  const [params] = useSearchParams();
  const sig = params.get("sig");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [choices, setChoices] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/meetings/${meetingId}/prevote`, { params: { sh: shareholderId, sig } });
      setData(data);
      const init = {};
      (data.agenda || []).forEach((a) => {
        if (a.my_choice) init[a.id] = a.my_choice;
      });
      setChoices(init);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  }, [meetingId, shareholderId, sig]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    const ballots = Object.entries(choices)
      .filter(([id]) => {
        const a = (data.agenda || []).find((x) => x.id === id);
        return a && !a.locked;
      })
      .map(([item_id, choice]) => ({ item_id, choice }));
    if (ballots.length === 0) {
      toast.error("Velg minst én stemme før du lagrer");
      return;
    }
    setSaving(true);
    try {
      const { data: res } = await api.post(`/meetings/${meetingId}/prevote`, { ballots }, { params: { sh: shareholderId, sig } });
      setData(res);
      setSaved(true);
      toast.success("Forhåndsstemmene dine er lagret");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-4">
        <div className="max-w-md rounded-2xl border border-border bg-card p-8 text-center" data-testid="prevote-error">
          <Lock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <h1 className="font-headings text-xl font-bold">Lenken kan ikke åpnes</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <p className="mt-4 text-xs text-muted-foreground">Be selskapet sende deg en ny innkalling, eller logg inn på Murphy Styrerom for å delta i generalforsamlingen.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const votable = (data.agenda || []).filter((a) => !a.locked);
  const allSubmitted = (data.agenda || []).every((a) => a.locked || choices[a.id]);

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="mx-auto max-w-2xl" data-testid="prevote-page">
        {/* Header */}
        <div className="overflow-hidden rounded-t-2xl bg-[#0f1a13] px-6 py-6 text-[#eaf87a]">
          <div className="text-2xl font-extrabold tracking-tight">murphy.</div>
          <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#cfe86a]">Forhåndsstemming til generalforsamling</div>
        </div>

        <div className="rounded-b-2xl border border-t-0 border-border bg-card p-6 sm:p-8">
          <h1 className="font-headings text-2xl font-extrabold tracking-tight" data-testid="prevote-title">{data.meeting.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {data.company.name}{data.company.org_number ? ` · Org.nr ${data.company.org_number}` : ""}
          </p>

          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {fmtDate(data.meeting.date)}</span>
            {data.meeting.location ? <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {data.meeting.location}</span> : null}
          </div>

          <div className="mt-5 flex items-center gap-3 rounded-xl border border-primary/25 bg-primary/[0.04] px-4 py-3" data-testid="prevote-shareholder">
            <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />
            <div className="text-sm">
              <span className="font-semibold">{data.shareholder.name}</span>
              <span className="text-muted-foreground"> · {nb(data.shareholder.shares)} aksjer</span>
            </div>
          </div>

          {(() => {
            const cd = countdownText(data.meeting.date);
            return (
              <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-sm" data-testid="prevote-deadline">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <div>
                  <span className="font-semibold">Frist for forhåndsstemming: </span>
                  {cd?.past ? (
                    "Møtet har startet – du kan bare stemme på saker som ennå ikke er tatt opp til avstemning."
                  ) : (
                    <>Åpen frem til hver sak tas opp i møtet, senest ved møtestart {fmtDate(data.meeting.date)}{cd ? ` (${cd.text})` : ""}.</>
                  )}
                </div>
              </div>
            );
          })()}

          <p className="mt-5 text-sm leading-relaxed text-foreground/80">
            Du kan avgi din stemme på sakene under før generalforsamlingen. Stemmen din regnes som representert og telles automatisk med når møteleder åpner avstemningen. Du kan endre stemmen din frem til avstemningen åpnes.
          </p>

          {/* Agenda */}
          <div className="mt-6 space-y-4" data-testid="prevote-agenda">
            {(data.agenda || []).map((a) => (
              <div key={a.id} className="rounded-xl border border-border bg-background p-4" data-testid={`prevote-item-${a.id}`}>
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 text-xs font-bold text-muted-foreground">{a.order}.</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-snug">{a.title}</p>
                    {a.description ? <p className="mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">{a.description}</p> : null}
                    {a.proposal ? (
                      <div className="mt-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs">
                        <span className="font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">Forslag til vedtak: </span>
                        {a.proposal}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-3">
                  {a.locked ? (
                    <div className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground" data-testid={`prevote-locked-${a.id}`}>
                      <Lock className="h-3.5 w-3.5" /> Avstemningen er åpnet i møtet – forhåndsstemming er stengt for denne saken.
                    </div>
                  ) : (
                    <ChoicePicker
                      value={choices[a.id]}
                      disabled={saving}
                      onPick={(c) => setChoices((prev) => ({ ...prev, [a.id]: c }))}
                      testidPrefix={`prevote-choice-${a.id}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          {votable.length === 0 ? (
            <div className="mt-6 rounded-xl border border-dashed border-border px-4 py-3 text-center text-sm text-muted-foreground">
              Alle avstemninger er allerede åpnet i møtet. Forhåndsstemming er ikke lenger mulig.
            </div>
          ) : (
            <div className="mt-6 flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                {saved ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Lagret. Du kan endre stemmen din når som helst frem til avstemningen åpnes.</span>
                ) : (
                  "Stemmene lagres først når du trykker «Lagre forhåndsstemmer»."
                )}
              </p>
              <Button onClick={submit} disabled={saving} className="gap-2" data-testid="prevote-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Vote className="h-4 w-4" />}
                {saved ? "Oppdater stemmer" : "Lagre forhåndsstemmer"}
              </Button>
            </div>
          )}

          <p className="mt-6 border-t border-border/60 pt-4 text-[11px] text-muted-foreground">
            Denne lenken er personlig og knyttet til deg som aksjeeier. Del den ikke med andre. Sendt via Murphy Styrerom.
          </p>
        </div>
      </div>
    </div>
  );
}
